import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { useGames } from '../contexts/DataContext';
import { useRecentGames } from '../hooks/useRecentGames';
import { GameOutput } from '../generated-api';

type Phase = 'selecting' | 'spinning' | 'revealed';

function formatLastPlayed(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}

const GamePickerPage: React.FC = () => {
  const navigate = useNavigate();
  const goBack = useCallback(() => navigate(-1), [navigate]);

  const { games, loading: gamesLoading, fetch: fetchGames } = useGames();
  const { recentGames } = useRecentGames(100);

  const [phase, setPhase] = useState<Phase>('selecting');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [ownerFilter, setOwnerFilter] = useState<string | null>(null);
  const [playerCountFilter, setPlayerCountFilter] = useState<string>('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Spinning state
  const [currentDisplayIndex, setCurrentDisplayIndex] = useState(0);
  const [winner, setWinner] = useState<GameOutput | null>(null);
  const spinIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const spinDelayRef = useRef(80);

  // Fetch games on mount
  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  // All unique owners across games
  const allOwners = useMemo(() => {
    return Array.from(new Set(games.flatMap(g => g.owners || []))).sort();
  }, [games]);

  // Filtered games
  const filteredGames = useMemo(() => {
    return games.filter(g => {
      if (searchQuery && !g.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (ownerFilter && !(g.owners || []).includes(ownerFilter)) return false;
      if (playerCountFilter) {
        const count = parseInt(playerCountFilter, 10);
        if (!isNaN(count) && (count < g.minTeams || count > g.maxTeams)) return false;
      }
      return true;
    });
  }, [games, searchQuery, ownerFilter, playerCountFilter]);

  // Last played lookup map
  const lastPlayedMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const rg of recentGames) {
      map.set(rg.game.id, rg.lastPlayedAt);
    }
    return map;
  }, [recentGames]);

  const toggleGame = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    const allFilteredIds = filteredGames.map(g => g.id);
    const allSelected = allFilteredIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        allFilteredIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        allFilteredIds.forEach(id => next.add(id));
        return next;
      });
    }
  }, [filteredGames, selectedIds]);

  const selectedGames = useMemo(() => {
    return games.filter(g => selectedIds.has(g.id));
  }, [games, selectedIds]);

  // --- Spinning Logic ---
  const startSpin = useCallback(() => {
    if (selectedGames.length < 2) return;

    // Pre-determine the winner
    const winnerIndex = Math.floor(Math.random() * selectedGames.length);
    const pickedGame = selectedGames[winnerIndex];
    setWinner(pickedGame);
    setPhase('spinning');
    setCurrentDisplayIndex(0);
    spinDelayRef.current = 80;

    let idx = 0;
    const tick = () => {
      idx = (idx + 1) % selectedGames.length;
      setCurrentDisplayIndex(idx);

      spinDelayRef.current = Math.min(spinDelayRef.current * 1.15, 550);

      if (spinDelayRef.current >= 500) {
        // Final stop — show the winner
        setCurrentDisplayIndex(selectedGames.indexOf(pickedGame));
        setTimeout(() => {
          setPhase('revealed');
        }, 400);
        return;
      }

      spinIntervalRef.current = setTimeout(tick, spinDelayRef.current);
    };

    spinIntervalRef.current = setTimeout(tick, spinDelayRef.current);
  }, [selectedGames]);

  // Cleanup spin interval on unmount
  useEffect(() => {
    return () => {
      if (spinIntervalRef.current) clearTimeout(spinIntervalRef.current);
    };
  }, []);

  const pickAgain = useCallback(() => {
    if (spinIntervalRef.current) clearTimeout(spinIntervalRef.current);
    startSpin();
  }, [startSpin]);

  const changeGames = useCallback(() => {
    if (spinIntervalRef.current) clearTimeout(spinIntervalRef.current);
    setPhase('selecting');
    setWinner(null);
  }, []);

  // Player range label
  const playerRange = (g: GameOutput) => {
    if (g.minTeams === g.maxTeams) return `${g.minTeams} players`;
    return `${g.minTeams}-${g.maxTeams} players`;
  };

  // --- Selecting Phase (normal light page) ---
  if (phase === 'selecting') {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col">
        {/* Header */}
        <div className="relative pt-14 sm:pt-4 pb-3 px-4 bg-surface border-b border-stone-200">
          <button
            onClick={goBack}
            className="absolute left-4 top-14 sm:top-4 p-2 -ml-2 text-body hover:text-heading"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          <h1 className="text-fluid-h2 font-bold text-heading text-center font-serif">Game Picker</h1>
          <p className="text-center text-sm text-stone-500 mt-1">Select games, then let fate decide</p>
        </div>

        {/* Search + Filter Toggle */}
        <div className="px-4 pt-3 pb-2 bg-surface border-b border-stone-200">
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                type="text"
                placeholder="Search games..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 text-sm border border-stone-300 rounded-xl bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <button
              onClick={() => setFiltersOpen(f => !f)}
              className={`p-2.5 rounded-xl border transition-colors ${
                filtersOpen || ownerFilter || playerCountFilter
                  ? 'border-primary-400 bg-primary-50 text-primary-700'
                  : 'border-stone-300 bg-stone-50 text-stone-500'
              }`}
            >
              <FunnelIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Collapsible Filters */}
          {filtersOpen && (
            <div className="mt-3 flex flex-col gap-2 pb-1">
              {allOwners.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-stone-500 block mb-1">Owner</label>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => setOwnerFilter(null)}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                        !ownerFilter
                          ? 'bg-primary-700 text-white border-primary-700'
                          : 'bg-surface text-stone-600 border-stone-300 hover:bg-stone-100'
                      }`}
                    >
                      All
                    </button>
                    {allOwners.map(owner => (
                      <button
                        data-track="picker_owner_filter"
                        key={owner}
                        onClick={() => setOwnerFilter(ownerFilter === owner ? null : owner)}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                          ownerFilter === owner
                            ? 'bg-primary-700 text-white border-primary-700'
                            : 'bg-surface text-stone-600 border-stone-300 hover:bg-stone-100'
                        }`}
                      >
                        {owner}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-stone-500 block mb-1">Player count</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  placeholder="e.g. 4"
                  value={playerCountFilter}
                  onChange={e => setPlayerCountFilter(e.target.value)}
                  className="w-24 px-3 py-2 text-sm border border-stone-300 rounded-xl bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Select All / Count */}
        <div className="px-4 py-2 flex items-center justify-between">
          <button
            onClick={toggleAll}
            className="text-sm font-medium text-primary-700 hover:text-primary-800"
          >
            {filteredGames.length > 0 && filteredGames.every(g => selectedIds.has(g.id))
              ? 'Deselect All'
              : 'Select All'}
          </button>
          <span className="text-sm text-stone-500">
            {selectedIds.size} game{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
        </div>

        {/* Game List */}
        <div className="flex-1 overflow-y-auto px-4 pb-40 sm:pb-28">
          {gamesLoading && games.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : filteredGames.length === 0 ? (
            <div className="text-center py-12 text-stone-400">
              <p className="text-lg font-medium">No games found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredGames.map(game => {
                const isSelected = selectedIds.has(game.id);
                return (
                  <button
                    data-track="picker_game_card"
                    key={game.id}
                    onClick={() => toggleGame(game.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left ${
                      isSelected
                        ? 'bg-primary-50 border-primary-300 shadow-sm'
                        : 'bg-surface border-stone-200 hover:bg-stone-50'
                    }`}
                  >
                    {/* Checkbox */}
                    <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      isSelected
                        ? 'bg-primary-600 border-primary-600'
                        : 'border-stone-300 bg-surface'
                    }`}>
                      {isSelected && (
                        <svg className="w-4 h-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>

                    {/* Game Image */}
                    {game.imageUrl ? (
                      <img
                        src={game.imageUrl}
                        alt={game.name}
                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-stone-200 flex items-center justify-center flex-shrink-0 text-xl">
                        🎲
                      </div>
                    )}

                    {/* Game Info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-stone-900 truncate">{game.name}</div>
                      <div className="text-xs text-stone-500 mt-0.5">
                        {playerRange(game)}
                        {game.owners && game.owners.length > 0 && (
                          <span className="ml-2 text-stone-400">
                            {game.owners.join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Bottom Action Bar — above mobile nav (h-20) */}
        <div className="fixed bottom-20 sm:bottom-0 left-0 right-0 z-40 bg-surface border-t border-stone-200 px-4 py-3">
          <button
            data-track="picker_spin"
            onClick={startSpin}
            disabled={selectedIds.size < 2}
            className="w-full py-3.5 bg-primary-600 hover:bg-primary-700 disabled:bg-stone-300 disabled:cursor-not-allowed text-white rounded-2xl font-bold text-lg shadow-lg transition-colors"
          >
            {selectedIds.size < 2
              ? `Select at least ${2 - selectedIds.size} more game${2 - selectedIds.size !== 1 ? 's' : ''}`
              : `Pick a Game!`}
          </button>
        </div>
      </div>
    );
  }

  // --- Spinning Phase (dark overlay) ---
  if (phase === 'spinning') {
    const displayGame = selectedGames[currentDisplayIndex];
    return (
      <div className="fixed inset-0 z-50 bg-stone-900 flex flex-col items-center justify-center">
        <style>{`
          @keyframes game-picker-pulse {
            0%, 100% { opacity: 0.6; }
            50% { opacity: 1; }
          }
          @media (prefers-reduced-motion: reduce) {
            .game-picker-spin-name {
              animation: none !important;
            }
          }
        `}</style>

        {/* Close Button */}
        <button
          onClick={goBack}
          className="absolute top-4 right-4 z-20 p-2 bg-stone-100/10 hover:bg-primary-500/20 rounded-full text-white transition-colors"
        >
          <XMarkIcon className="h-8 w-8" />
        </button>

        {/* Title */}
        <div className="absolute top-8 left-0 right-0 text-center">
          <h2 className="text-xl font-serif font-bold text-white/80">Game Picker</h2>
        </div>

        {/* Spinning Window */}
        <div className="flex flex-col items-center gap-6">
          {/* Glow ring */}
          <div
            className="w-48 h-48 rounded-full flex items-center justify-center"
            style={{
              background: 'radial-gradient(circle, rgba(251,191,36,0.15) 0%, transparent 70%)',
              boxShadow: '0 0 60px rgba(251,191,36,0.2)',
            }}
          >
            {displayGame?.imageUrl ? (
              <img
                src={displayGame.imageUrl}
                alt=""
                className="w-32 h-32 rounded-2xl object-cover"
                style={{ opacity: 0.8 }}
              />
            ) : (
              <div className="w-32 h-32 rounded-2xl bg-stone-800 flex items-center justify-center text-5xl">
                🎲
              </div>
            )}
          </div>

          {/* Current Game Name */}
          <div
            className="game-picker-spin-name text-3xl font-bold text-white text-center px-8 min-h-[2.5rem]"
            style={{
              textShadow: '0 0 20px rgba(251,191,36,0.5)',
              animation: 'game-picker-pulse 0.3s ease-in-out infinite',
            }}
          >
            {displayGame?.name || '...'}
          </div>
        </div>
      </div>
    );
  }

  // --- Revealed Phase (dark overlay) ---
  if (phase === 'revealed' && winner) {
    const lastPlayedDate = lastPlayedMap.get(winner.id);
    return (
      <div className="fixed inset-0 z-50 bg-stone-900 flex flex-col items-center justify-center">
        <style>{`
          @keyframes game-picker-bounce {
            0% { transform: scale(0.3); opacity: 0; }
            50% { transform: scale(1.08); }
            70% { transform: scale(0.95); }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes game-picker-fade-up {
            0% { transform: translateY(20px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          @media (prefers-reduced-motion: reduce) {
            .game-picker-reveal, .game-picker-reveal-info {
              animation: none !important;
              opacity: 1 !important;
              transform: none !important;
            }
          }
        `}</style>

        {/* Close Button */}
        <button
          onClick={goBack}
          className="absolute top-4 right-4 z-20 p-2 bg-stone-100/10 hover:bg-primary-500/20 rounded-full text-white transition-colors"
        >
          <XMarkIcon className="h-8 w-8" />
        </button>

        {/* Title */}
        <div className="absolute top-8 left-0 right-0 text-center">
          <h2 className="text-xl font-serif font-bold text-white/80">You're playing...</h2>
        </div>

        {/* Revealed Game */}
        <div
          className="game-picker-reveal flex flex-col items-center gap-6 px-6"
          style={{ animation: 'game-picker-bounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' }}
        >
          {/* Game Image */}
          {winner.imageUrl ? (
            <img
              src={winner.imageUrl}
              alt={winner.name}
              className="w-48 h-48 rounded-2xl object-cover shadow-2xl"
              style={{ boxShadow: '0 0 40px rgba(251,191,36,0.3)' }}
            />
          ) : (
            <div
              className="w-48 h-48 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-7xl shadow-2xl"
            >
              🎲
            </div>
          )}

          {/* Game Name Card */}
          <div className="bg-amber-50 px-8 py-4 rounded-2xl shadow-xl border border-stone-200 max-w-sm text-center">
            <h3 className="text-4xl font-black font-serif text-stone-900">{winner.name}</h3>
          </div>

          {/* Details */}
          <div
            className="game-picker-reveal-info flex flex-col items-center gap-1.5"
            style={{ animation: 'game-picker-fade-up 0.4s ease-out 0.4s forwards', opacity: 0 }}
          >
            <span className="text-stone-400 text-sm">{playerRange(winner)}</span>
            <span className="text-stone-400 text-sm">
              {lastPlayedDate
                ? `Last played ${formatLastPlayed(lastPlayedDate)}`
                : 'Never played'}
            </span>
            {winner.owners && winner.owners.length > 0 && (
              <span className="text-stone-500 text-xs">
                Owned by {winner.owners.join(', ')}
              </span>
            )}
          </div>
        </div>

        {/* Bottom Buttons */}
        <div
          className="absolute bottom-0 left-0 right-0 flex flex-col items-center gap-3 pb-24 sm:pb-12 px-6"
          style={{ animation: 'game-picker-fade-up 0.4s ease-out 0.6s forwards', opacity: 0 }}
        >
          <button
            onClick={() => navigate(`/plays/new?gameId=${winner.id}`)}
            className="w-full max-w-xs py-3 bg-primary-700 hover:bg-primary-800 text-white rounded-2xl font-bold text-base shadow-xl transition hover:scale-105 active:scale-95"
          >
            Start Game
          </button>
          <div className="flex gap-3">
            <button
              onClick={changeGames}
              className="px-5 py-3 bg-stone-600 hover:bg-stone-700 text-white rounded-2xl font-bold text-base shadow-xl transition hover:scale-105 active:scale-95"
            >
              Change Games
            </button>
            <button
              onClick={pickAgain}
              className="px-5 py-3 bg-stone-700 hover:bg-stone-600 text-white rounded-2xl font-bold text-base shadow-xl transition hover:scale-105 active:scale-95"
            >
              Pick Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default GamePickerPage;
